import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import EventService from "../../services/EventService";
import AddToCalendarButton from "./AddToCalendarButton";
import EventShell, { EventError, EventLoading } from "./EventShell";
import { recallRegistration } from "./EventRegister";
import {
  formatEventRange,
  googleCalendarUrl,
  timezoneNote,
} from "./eventFormat";

/**
 * Confirmation / ticket page at /events/:slug/confirmed?t=<token>.
 *
 * Falls back to the locally remembered token so a refresh, or a return visit
 * from a different tab, still lands on the ticket rather than a dead end.
 */
const EventConfirmation = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || recallRegistration(slug);

  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError(
        "We couldn't find your registration. Check the link in your confirmation email.",
      );
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const result = await EventService.getRegistration(token);
      if (!active) return;
      if (result.success) {
        setRegistration(result.registration);
      } else {
        setError(result.message || "We couldn't find your registration.");
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <EventShell narrow>
        <EventLoading label="Loading your ticket…" />
      </EventShell>
    );
  }

  if (error || !registration) {
    return (
      <EventShell narrow>
        <EventError message={error} />
        <Link className="ev-btn ev-btn-secondary" to={`/events/${slug}`}>
          Back to the event
        </Link>
      </EventShell>
    );
  }

  const waitlisted = registration.status === "WAITLISTED";
  const calendarEvent = {
    title: registration.eventTitle,
    slug: registration.eventSlug,
    startsAt: registration.eventStartsAt,
    endsAt: registration.eventEndsAt,
    venueName: registration.venueName,
  };

  return (
    <EventShell
      narrow
      crumbs={[
        { label: "Events", to: "/events" },
        { label: registration.eventTitle, to: `/events/${slug}` },
        { label: waitlisted ? "Waitlist" : "Confirmed" },
      ]}
    >
      <h1 className="ev-h1">
        {waitlisted ? "You're on the waitlist" : "You're registered"}
      </h1>
      <p className="ev-lead">
        {waitlisted
          ? "We'll email you right away if a seat opens up."
          : `We've emailed your ticket to ${registration.email}.`}
      </p>

      <div className="ev-panel ev-panel-accent">
        <div className="ev-facts">
          <div className="ev-fact">
            <span className="ev-fact-label">Event</span>
            <span>
              <strong>{registration.eventTitle}</strong>
            </span>
          </div>
          <div className="ev-fact">
            <span className="ev-fact-label">When</span>
            <span>
              {formatEventRange(
                registration.eventStartsAt,
                registration.eventEndsAt,
              )}
              {timezoneNote(
                registration.eventTimezone,
                registration.eventStartsAt,
              ) && (
                <div className="ev-hint">
                  {timezoneNote(
                    registration.eventTimezone,
                    registration.eventStartsAt,
                  )}
                </div>
              )}
            </span>
          </div>
          <div className="ev-fact">
            <span className="ev-fact-label">Where</span>
            <span>
              {registration.eventFormat === "VIRTUAL"
                ? "Online — join link released shortly before the start"
                : registration.venueName || "Venue to be announced"}
            </span>
          </div>
          <div className="ev-fact">
            <span className="ev-fact-label">Attending</span>
            <span>
              {registration.partySize}{" "}
              {registration.partySize === 1 ? "person" : "people"}
            </span>
          </div>
        </div>
      </div>

      {!waitlisted && (
        <div className="ev-ticket">
          <p className="ev-ticket-label">Confirmation code</p>
          <p className="ev-ticket-code">{registration.confirmationCode}</p>
          <p className="ev-hint" style={{ marginTop: 12, marginBottom: 0 }}>
            Show this code at check-in. We can also look you up by name.
          </p>
        </div>
      )}

      <div className="ev-btn-row">
        <AddToCalendarButton
          slug={registration.eventSlug}
          className="ev-btn ev-btn-primary"
        />
        <a
          className="ev-btn ev-btn-secondary"
          href={googleCalendarUrl(calendarEvent)}
          target="_blank"
          rel="noreferrer noopener"
        >
          Google Calendar
        </a>
        <Link
          className="ev-btn ev-btn-quiet"
          to={`/events/registration/${token}`}
        >
          Manage registration
        </Link>
      </div>

      {registration.demoSeatEligible && (
        <div className="ev-panel">
          <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
            Try KinShield during the session
          </h2>
          <p className="ev-muted">
            We can set you up with a full KinShield account to use during the
            event. Verify your email address on the manage page to claim one.
          </p>
          <Link
            className="ev-btn ev-btn-secondary"
            to={`/events/registration/${token}`}
          >
            Claim a demo account
          </Link>
        </div>
      )}

      <div className="ev-panel">
        <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
          Bring someone with you
        </h2>
        <p className="ev-muted">
          Scams spread through families. Share this link with someone who'd
          benefit.
        </p>
        <ShareLink slug={registration.eventSlug} />
      </div>
    </EventShell>
  );
};

const ShareLink = ({ slug }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/events/${slug}?source=referral`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="ev-btn-row" style={{ marginBottom: 0 }}>
      <button type="button" className="ev-btn ev-btn-secondary" onClick={copy}>
        {copied ? "Link copied" : "Copy invite link"}
      </button>
      <a
        className="ev-btn ev-btn-quiet"
        href={`mailto:?subject=${encodeURIComponent("A free session on avoiding scams")}&body=${encodeURIComponent(url)}`}
      >
        Email it
      </a>
    </div>
  );
};

export default EventConfirmation;
